import { env } from "@/config/env";
import React, { useEffect, useRef } from "react";
import Map, { MapRef, NavigationControl, MapProps } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { initialMapState, mapStyles } from "@/config/map";
import { useSidebar } from "../ui/sidebar";
import { Spinner } from "@/components/Spinner";
import { waterMessages } from "../../utils/loadingMessages";
import { MapLoadingProps } from "@/types/map";
import { cn } from "@/lib/utils";

type MapBaseProps = {
	children?: React.ReactNode;
	isStatic?: boolean;
	mapRef?: React.Ref<MapRef>;
	loader?: MapLoadingProps;
} & Partial<MapProps>;

const getRefCurrent = (ref: React.Ref<MapRef> | undefined): MapRef | null => {
	if (!ref) return null;
	if (typeof ref === "function") return null;
	return ref.current ?? null;
};

export const MapBase = ({
	children,
	isStatic = false,
	mapRef,
	initialViewState = initialMapState,
	mapStyle = mapStyles[0].url,
	loader,
	...rest
}: MapBaseProps) => {
	const { isLoading = false, spinner, className = "" } = loader ?? {};

	const { isMobile, open, openMobile } = useSidebar();
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const timeout = setTimeout(() => {
			const map = getRefCurrent(mapRef);
			if (map) map.resize();
		}, 300);
		return () => clearTimeout(timeout);
	}, [open, openMobile, isMobile, mapRef]);

	useEffect(() => {
		const map = getRefCurrent(mapRef);
		if (!containerRef.current || !map) return;

		const resizeObserver = new ResizeObserver(() => {
			map.resize();
		});

		resizeObserver.observe(containerRef.current);
		return () => resizeObserver.disconnect();
	}, [mapRef]);

	const interactionProps = isStatic
		? {
				dragPan: false,
				dragRotate: false,
				scrollZoom: false,
				touchZoomRotate: false,
				doubleClickZoom: false,
				interactive: false,
			}
		: {};

	return (
		<div ref={containerRef} className="w-full h-full">
			<Map
				mapboxAccessToken={env.MAPBOX_TOKEN}
				initialViewState={initialViewState}
				projection={{ name: "globe" }}
				mapStyle={mapStyle}
				style={{
					width: "100%",
					height: "100%",
				}}
				ref={mapRef}
				{...interactionProps}
				{...rest}
			>
				{!isStatic && <NavigationControl position="bottom-left" />}
				{children}
			</Map>
			{isLoading && (
				<div
					className={cn("absolute inset-0 flex items-center justify-center bg-black/60 z-50", className)}
					aria-live="polite"
					role="status"
				>
					<Spinner
						messages={waterMessages}
						intervalMs={2000}
						size={30}
						loaderClassName="text-white"
						messageClassName="text-white opacity-80 text-xs tracking-wide font-mono"
						{...spinner}
					/>
				</div>
			)}
		</div>
	);
};
